import { useEffect, useRef, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { UploadCloud, Loader2, Archive, RotateCcw, Pencil, RefreshCw, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { apiFetch, uploadFile } from "@/lib/api";
import { MOTIF_PACKS } from "@/lib/appearance";

const ACCEPT = ".png,.webp,.svg,image/png,image/webp,image/svg+xml";
const PACK_LABEL = { shiva: "Shiva", lalita: "Lalita", vishnu: "Vishnu", universal: "Universal", devotional: "Devotional", custom: "Custom" };

// Admin-only motif asset library. Empty by default — no seeded imagery.
export function MotifLibrary({ onChanged }) {
  const [motifs, setMotifs] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null);   // motif being edited (or {new:true})
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const replaceRef = useRef(null);
  const replaceId = useRef(null);

  const load = useCallback(async () => {
    try { setMotifs(await apiFetch(`/motifs?status=${showArchived ? "all" : "active"}`)); }
    catch (e) { toast.error(e.message); }
  }, [showArchived]);
  useEffect(() => { load(); }, [load]);

  const onPickNew = () => fileRef.current?.click();
  const onNewFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    // stage a new motif with the file, open editor to collect metadata
    setEditing({ new: true, file, name: file.name.replace(/\.[^.]+$/, ""), category: "", description: "", packs: [], tint_compat: "any" });
  };

  const saveNew = async (draft) => {
    setBusy(true);
    try {
      await uploadFile("/motifs", draft.file, { fields: {
        name: draft.name, category: draft.category, description: draft.description,
        packs: (draft.packs || []).join(","), tint_compat: draft.tint_compat } });
      toast.success("Motif uploaded"); setEditing(null); await load(); onChanged?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const saveEdit = async (draft) => {
    setBusy(true);
    try {
      await apiFetch(`/motifs/${draft.id}`, { method: "PUT", body: JSON.stringify({
        name: draft.name, category: draft.category, description: draft.description,
        packs: draft.packs, tint_compat: draft.tint_compat }) });
      toast.success("Motif updated"); setEditing(null); await load(); onChanged?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const onReplaceFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file || !replaceId.current) return;
    setBusy(true);
    try { await uploadFile(`/motifs/${replaceId.current}/replace`, file); toast.success("Artwork replaced"); await load(); onChanged?.(); }
    catch (err) { toast.error(err.message); } finally { setBusy(false); replaceId.current = null; }
  };
  const setArchived = async (m, archived) => {
    try {
      await apiFetch(`/motifs/${m.id}/archive`, { method: "PUT", body: JSON.stringify({ archived }) });
      toast.success(archived ? "Motif archived" : "Motif restored"); await load(); onChanged?.();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Card className="p-5 shadow-soft" data-testid="motif-library">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg font-semibold">Motif library</h3>
          <p className="text-xs text-muted-foreground">Upload approved SVG, PNG or WebP artwork. Empty by default — nothing is shipped automatically.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setShowArchived((s) => !s)} data-testid="motif-toggle-archived">
            {showArchived ? "Hide archived" : "Show archived"}
          </Button>
          <Button size="sm" className="gap-1.5" onClick={onPickNew} data-testid="motif-upload">
            <UploadCloud className="h-4 w-4" /> Upload motif
          </Button>
        </div>
      </div>
      <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={onNewFile} data-testid="motif-file-input" />
      <input ref={replaceRef} type="file" accept={ACCEPT} className="hidden" onChange={onReplaceFile} />

      {motifs.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-10 text-center" data-testid="motif-empty">
          <ImageOff className="mb-2 h-7 w-7 text-muted-foreground" strokeWidth={1.5} />
          <p className="text-sm font-medium">No motifs yet</p>
          <p className="mt-1 max-w-xs text-xs text-muted-foreground">When Nirvana's approved sacred artwork is ready, upload it here to use across Atmosphere placements.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="motif-grid">
          {motifs.map((m) => (
            <div key={m.id} className="group rounded-lg border border-border p-2" data-testid={`motif-${m.id}`}>
              <div className="mb-2 flex h-24 items-center justify-center overflow-hidden rounded bg-muted/40 p-3">
                {m.url ? <img src={m.url} alt={m.name} className="max-h-full max-w-full object-contain opacity-80" /> : <ImageOff className="h-6 w-6 text-muted-foreground" />}
              </div>
              <div className="truncate text-sm font-medium" title={m.name}>{m.name}</div>
              <div className="mt-0.5 flex flex-wrap gap-1">
                {(m.packs || []).map((p) => <Badge key={p} variant="secondary" className="text-[10px]">{PACK_LABEL[p] || p}</Badge>)}
                {m.status === "archived" && <Badge className="bg-muted text-[10px] text-muted-foreground">Archived</Badge>}
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setEditing({ ...m })} data-testid={`motif-edit-${m.id}`}><Pencil className="mr-1 h-3 w-3" />Edit</Button>
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => { replaceId.current = m.id; replaceRef.current?.click(); }}><RefreshCw className="mr-1 h-3 w-3" />Replace</Button>
                {m.status === "archived"
                  ? <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setArchived(m, false)}><RotateCcw className="mr-1 h-3 w-3" />Restore</Button>
                  : <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-destructive" onClick={() => setArchived(m, true)}><Archive className="mr-1 h-3 w-3" />Archive</Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <MotifEditor draft={editing} busy={busy}
          onCancel={() => setEditing(null)}
          onSave={(d) => (d.new ? saveNew(d) : saveEdit(d))} />
      )}
    </Card>
  );
}

function MotifEditor({ draft, onSave, onCancel, busy }) {
  const [d, setD] = useState(draft);
  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));
  const togglePack = (p) => setD((s) => ({ ...s, packs: (s.packs || []).includes(p) ? s.packs.filter((x) => x !== p) : [...(s.packs || []), p] }));
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md" data-testid="motif-editor">
        <DialogHeader><DialogTitle>{d.new ? "New motif" : "Edit motif"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Name</Label><Input value={d.name} onChange={(e) => set("name", e.target.value)} data-testid="motif-name" /></div>
          <div><Label className="text-xs">Category / tradition</Label><Input value={d.category} onChange={(e) => set("category", e.target.value)} placeholder="e.g. Shaiva" data-testid="motif-category" /></div>
          <div><Label className="text-xs">Description (optional)</Label><Textarea rows={2} value={d.description} onChange={(e) => set("description", e.target.value)} /></div>
          <div>
            <Label className="text-xs">Motif packs</Label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {MOTIF_PACKS.map((p) => {
                const on = (d.packs || []).includes(p);
                return <button key={p} type="button" onClick={() => togglePack(p)} data-testid={`motif-pack-${p}`}
                  className={`rounded-full border px-2.5 py-1 text-xs ${on ? "border-accent bg-accent-subtle" : "border-border text-muted-foreground"}`}>{PACK_LABEL[p]}</button>;
              })}
            </div>
          </div>
          <div>
            <Label className="text-xs">Tint compatibility</Label>
            <Select value={d.tint_compat} onValueChange={(v) => set("tint_compat", v)}>
              <SelectTrigger data-testid="motif-tint"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Works on light &amp; dark</SelectItem>
                <SelectItem value="light">Best on light</SelectItem>
                <SelectItem value="dark">Best on dark</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button onClick={() => onSave(d)} disabled={busy || !d.name?.trim()} data-testid="motif-save">
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{d.new ? "Upload" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
